/**
 * The contacts editor — one card per person, shared by the "Add an
 * Account" dialog and the account page's "Edit contacts" dialog.
 *
 * WHY A PERSON GETS A CARD AND A PHONE NUMBER DOESN'T
 *
 * A bar has an owner and a bar manager; a chain store has a buyer and
 * whoever answers the phone. Reps were putting the second person into
 * free-text notes, where nothing can dial or search them. So the unit
 * here is the person: they own their numbers and addresses, which are
 * plain ordered lists inside the card, best first.
 *
 * WHY THE ROWS CARRY A `key` OF THEIR OWN
 *
 * Cards and rows get added and removed mid-edit. Keying them by array
 * index means React reuses the wrong input when a row is deleted —
 * the value follows the array but the focus and IME state don't. Every
 * draft therefore carries a client-only id that lives as long as the
 * row does.
 */

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';

import {
  EMAIL_SHAPE,
  MAX_CONTACTS,
  MAX_VALUES_PER_CONTACT,
  emptyContactDraft,
  type ContactDraft,
} from './contactDrafts';
import {
  DANGER,
  FIELD_SX,
  FONT_INTER,
  GOLD,
  HAIRLINE,
  HELPER_SX,
  QUIET_ACTION_SX,
} from './formStyles';

interface ValueListProps {
  label: string;
  type: 'tel' | 'email';
  values: string[];
  onChange: (next: string[]) => void;
  addLabel: string;
  validate?: (value: string) => boolean;
  errorText?: string;
}

/** The phone list and the email list are the same control twice. */
function ValueList({
  label,
  type,
  values,
  onChange,
  addLabel,
  validate,
  errorText,
}: ValueListProps) {
  const setAt = (index: number, value: string) =>
    onChange(values.map((v, i) => (i === index ? value : v)));

  // Removing the last remaining box leaves an empty one rather than no
  // field at all — it's part of the card, not something to re-summon.
  const removeAt = (index: number) =>
    onChange(values.length === 1 ? [''] : values.filter((_, i) => i !== index));

  return (
    <Stack gap={1.25}>
      {values.map((value, index) => {
        const invalid = validate ? value.trim().length > 0 && !validate(value.trim()) : false;
        return (
          <TextField
            // Index keys are safe here and only here: the value IS the
            // array entry, so a shifted row shows the right text.
            key={index}
            label={index === 0 ? label : `${label} ${index + 1}`}
            type={type}
            value={value}
            onChange={(e) => setAt(index, e.target.value)}
            size="small"
            fullWidth
            error={invalid}
            helperText={invalid ? errorText : undefined}
            FormHelperTextProps={HELPER_SX}
            inputProps={
              type === 'email'
                ? { inputMode: 'email', autoCapitalize: 'none', spellCheck: false }
                : { inputMode: 'tel' }
            }
            InputProps={{
              // The remove control sits INSIDE the input. Beside it, a
              // button would shorten every box by its own width and
              // leave the column stopping short of the fields above.
              endAdornment:
                values.length > 1 ? (
                  <IconButton
                    onClick={() => removeAt(index)}
                    aria-label={`Remove ${label.toLowerCase()} ${index + 1}`}
                    size="small"
                    edge="end"
                    sx={{
                      color: 'rgba(15, 10, 7, 0.35)',
                      transition: 'color 160ms ease, background-color 160ms ease',
                      '&:hover': { color: DANGER, bgcolor: 'rgba(192, 73, 47, 0.08)' },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                ) : undefined,
            }}
            sx={FIELD_SX}
          />
        );
      })}

      {values.length < MAX_VALUES_PER_CONTACT && (
        <Box>
          <Button
            onClick={() => onChange([...values, ''])}
            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
            size="small"
            sx={{ ...QUIET_ACTION_SX, ml: -1 }}
          >
            {addLabel}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

interface Props {
  value: ContactDraft[];
  onChange: (next: ContactDraft[]) => void;
}

export function ContactsEditor({ value, onChange }: Props) {
  const patch = (key: string, changes: Partial<ContactDraft>) =>
    onChange(value.map((d) => (d.key === key ? { ...d, ...changes } : d)));

  const removeCard = (key: string) => {
    const remaining = value.filter((d) => d.key !== key);
    onChange(remaining.length > 0 ? remaining : [emptyContactDraft()]);
  };

  return (
    <Stack gap={2}>
      {value.map((draft, index) => (
        <Box
          key={draft.key}
          sx={{
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 2.5,
            p: { xs: 1.75, sm: 2 },
            bgcolor: '#fff',
            transition: 'border-color 180ms ease',
            '&:hover': { borderColor: 'rgba(187, 140, 63, 0.38)' },
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <PersonOutlineIcon sx={{ fontSize: 15, color: GOLD }} />
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'rgba(15, 10, 7, 0.45)',
                }}
              >
                {/* The first card is the one to try first — worth
                    saying, since the order is what carries that. */}
                {index === 0 ? 'Primary contact' : `Contact ${index + 1}`}
              </Typography>
            </Stack>

            {value.length > 1 && (
              <Tooltip title="Remove this contact">
                <IconButton
                  onClick={() => removeCard(draft.key)}
                  aria-label={`Remove contact ${index + 1}`}
                  size="small"
                  sx={{
                    color: 'rgba(15, 10, 7, 0.35)',
                    '&:hover': { color: DANGER, bgcolor: 'rgba(192, 73, 47, 0.08)' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          <Stack gap={1.75}>
            <Box
              sx={{
                display: 'grid',
                gap: 1.75,
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              }}
            >
              <TextField
                label="Name"
                value={draft.name}
                onChange={(e) => patch(draft.key, { name: e.target.value })}
                size="small"
                fullWidth
                placeholder="Who did you speak to?"
                sx={FIELD_SX}
              />
              <TextField
                label="Role (optional)"
                value={draft.role}
                onChange={(e) => patch(draft.key, { role: e.target.value })}
                size="small"
                fullWidth
                placeholder="Owner, bar manager…"
                sx={FIELD_SX}
              />
            </Box>

            <ValueList
              label="Phone"
              type="tel"
              values={draft.phones}
              onChange={(phones) => patch(draft.key, { phones })}
              addLabel="Add Another Phone"
            />

            <ValueList
              label="Email"
              type="email"
              values={draft.emails}
              onChange={(emails) => patch(draft.key, { emails })}
              addLabel="Add Another Email"
              validate={(v) => EMAIL_SHAPE.test(v)}
              errorText="That doesn’t look like an email address."
            />
          </Stack>
        </Box>
      ))}

      {value.length < MAX_CONTACTS && (
        <Box>
          <Button
            onClick={() => onChange([...value, emptyContactDraft()])}
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            size="small"
            sx={{ ...QUIET_ACTION_SX, fontSize: 13, ml: -1 }}
          >
            Add Another Contact
          </Button>
        </Box>
      )}
    </Stack>
  );
}
